<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-container fluid>
    <h2 class="font-weight-bold mb-4">{{ $t("quick_analysis") }}</h2>
    <v-row dense>
      <v-col cols="12" sm="6" md="3" v-for="(card, i) in quickStats" :key="i">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">
            {{ $t(card.title) }}
          </div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ card.value }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <h3 class="font-weight-bold mt-10">{{ $t("customised_analysis") }}</h3>
    <v-row class="mb-4">
      <v-col cols="12" sm="3">
        <v-menu
          ref="fromMenu"
          v-model="fromMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="customFrom"
              :label="$t('from')"
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="customFrom"
            @input="fromMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="3">
        <v-menu
          ref="toMenu"
          v-model="toMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="customTo"
              :label="$t('to')"
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="customTo"
            @input="toMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="3">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">{{ $t("income") }}</div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ customStats.income }} IQD
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">
            {{ $t("expenses") }}
          </div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ customStats.expenses }} IQD
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">
            {{ $t("total_purchase_price") }}
          </div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ customStats.total_purchase_price_per_date_range }} IQD
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">
            {{ $t("total_selling_price") }}
          </div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ customStats.total_selling_price_per_date_range }} IQD
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card class="pa-4" outlined elevation="2" color="#efefef">
          <div class="text-subtitle-2 text--secondary">
            {{ $t("net_profit") }}
          </div>
          <div class="text-h6 font-weight-bold mt-1">
            {{ customStats.net_profit_per_date_range }} IQD
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "../services/axios"; // Adjust based on your axios setup

export default {
  data() {
    return {
      customFrom: null,
      customTo: null,
      fromMenu: false,
      toMenu: false,
      customStats: {
        income: 0,
        expenses: 0,
        total_purchase_price_per_date_range: 0,
        total_selling_price_per_date_range: 0,
        net_profit_per_date_range: 0,
      },
      quickStats: [],
      series: [
        {
          name: "Income",
          data: [],
        },
        {
          name: "Spent",
          data: [],
        },
      ],
      chartOptions: {
        chart: {
          type: "line",
          toolbar: { show: false },
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        colors: ["#1E88E5", "#E53935"],
        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
        },
        legend: {
          position: "top",
        },
        dataLabels: {
          enabled: false,
        },
        grid: {
          borderColor: "#e7e7e7",
        },
      },
    };
  },
  methods: {
    async fetchDashboardData() {
      try {
        const params = {};
        if (this.customFrom) params.start_date = this.customFrom;
        if (this.customTo) params.end_date = this.customTo;

        const res = await api.get("/dashboard", { params });
        const data = res.data.data;

        // Update customStats for date range section
        this.customStats.income = data.total_income_per_date_range || 0;
        this.customStats.expenses = data.total_expense_per_date_range || 0;
        this.customStats.total_purchase_price_per_date_range = data.total_purchase_price_per_date_range || 0;
        this.customStats.total_selling_price_per_date_range = data.total_selling_price_per_date_range || 0;
        this.customStats.net_profit_per_date_range = data.net_profit_per_date_range || 0;

        // Update quickStats
        this.quickStats = [
          {
            title: "total_income_year",
            value: `${data.total_income_per_year} IQD`,
          },
          {
            title: "total_spent_year",
            value: `${data.total_expense_per_year} IQD`,
          },
          {
            title: "total_income_month",
            value: `${data.total_income_per_month} IQD`,
          },
          {
            title: "total_spent_month",
            value: `${data.total_expense_per_month} IQD`,
          },
          { title: "number_of_products", value: data.total_products },
          { title: "number_of_categories", value: data.total_categories },
          { title: "total_purchase_price", value: data.total_purchase_price },
          { title: "total_selling_price", value: data.total_selling_price },
          { title: "net_profit_per_month", value: data.net_profit_per_month },
          { title: "net_profit_per_year", value: data.net_profit_per_year }
        ];

        // Optionally update chart series if your API returns trends
        this.series = [
          {
            name: "Income",
            data: [
              data.total_income_per_year,
              data.total_income_per_month,
              data.total_income_per_date_range,
            ],
          },
          {
            name: "Spent",
            data: [
              data.total_expense_per_year,
              data.total_expense_per_month,
              data.total_expense_per_date_range,
            ],
          },
        ];
      } catch (err) {
        console.error("Dashboard fetch error", err);
      }
    },
  },
  watch: {
    customFrom: "fetchDashboardData",
    customTo: "fetchDashboardData",
  },
  mounted() {
    this.fetchDashboardData();
  },
};
</script>

<style scoped></style>
